import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { bookingsAPI, roomsAPI } from "../../services/meetingsAPI";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Calendar from "react-calendar";
import { getImageUrl } from "../../utils/imageHelper";
import "react-calendar/dist/Calendar.css";
import "./MyBookings.css";

const MyBookings = () => {
  const ROOM_IMAGE_FALLBACK =
    "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1200&q=80";
  const navigate = useNavigate();
  // Get user from localStorage instead of AuthContext
  const getUser = () => {
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      return null;
    }
  };
  const user = getUser();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [customReason, setCustomReason] = useState("");

  // Cancel modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [canceledBy, setCanceledBy] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewBooking, setViewBooking] = useState(null);

  // Calendar state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);

  // State for Read More / Read Less in booking cards
  const [expandedMembers, setExpandedMembers] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const alarmAudioContextRef = useRef(null);
  const alarmStoreKey = `meetingAlarmTriggered_${user?._id || user?.id || "guest"}`;

  const isApprovedStatus = (status) =>
    ["approved", "confirmed"].includes(String(status || "").toLowerCase());

  // Toggle function
  const toggleMemberExpand = (bookingId) => {
    setExpandedMembers((prev) => ({
      ...prev,
      [bookingId]: !prev[bookingId],
    }));
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    const pollingInterval = setInterval(() => {
      fetchBookings(false);
    }, 20000);
    return () => clearInterval(pollingInterval);
  }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const fetchBookings = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const response = await bookingsAPI.getMyBookings();
      const rawBookings = Array.isArray(response.data) ? response.data : [];
      const roomIdsToHydrate = Array.from(
        new Set(
          rawBookings
            .map((booking) => {
              const roomId = booking?.room?._id || booking?.room?.id;
              const hasImages =
                Array.isArray(booking?.room?.images) && booking.room.images.length > 0;
              return roomId && !hasImages ? String(roomId) : null;
            })
            .filter(Boolean)
        )
      );

      if (!roomIdsToHydrate.length) {
        setBookings(rawBookings);
        return;
      }

      const roomEntries = await Promise.all(
        roomIdsToHydrate.map(async (roomId) => {
          try {
            const roomResponse = await roomsAPI.getRoom(roomId);
            return [roomId, roomResponse.data];
          } catch {
            return [roomId, null];
          }
        })
      );

      const roomMap = new Map(roomEntries);
      const hydratedBookings = rawBookings.map((booking) => {
        const roomId = booking?.room?._id || booking?.room?.id;
        const fullRoom = roomId ? roomMap.get(String(roomId)) : null;
        if (!fullRoom) {
          return booking;
        }

        return {
          ...booking,
          room: {
            ...fullRoom,
            ...booking.room,
            images:
              (Array.isArray(booking?.room?.images) && booking.room.images.length > 0)
                ? booking.room.images
                : fullRoom.images || [],
          },
        };
      });

      setBookings(hydratedBookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const playAlarmSound = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      if (!alarmAudioContextRef.current) {
        alarmAudioContextRef.current = new AudioCtx();
      }

      const ctx = alarmAudioContextRef.current;
      const now = ctx.currentTime;

      const tone = (startAt, duration, frequency) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(frequency, startAt);
        gain.gain.setValueAtTime(0.001, startAt);
        gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startAt);
        osc.stop(startAt + duration);
      };

      tone(now, 0.24, 880);
      tone(now + 0.32, 0.24, 988);
      tone(now + 0.64, 0.42, 1175);
    } catch (error) {
      console.error("Failed to play alarm sound:", error);
    }
  }, []);

  const triggerDueMeetingAlarms = useCallback(() => {
    if (!Array.isArray(bookings) || bookings.length === 0) return;

    const nowMs = Date.now();
    const lookbackMs = nowMs - 60 * 1000;
    const nearFutureMs = nowMs + 10 * 1000;
    let firedMap = {};

    try {
      firedMap = JSON.parse(localStorage.getItem(alarmStoreKey) || "{}");
    } catch {
      firedMap = {};
    }

    const dueBookings = bookings.filter((booking) => {
      if (!isApprovedStatus(booking.status)) return false;
      const startMs = new Date(booking.startTime).getTime();
      if (!Number.isFinite(startMs)) return false;
      if (startMs < lookbackMs || startMs > nearFutureMs) return false;
      const bookingId = String(booking._id || booking.id || "");
      return bookingId && !firedMap[bookingId];
    });

    if (!dueBookings.length) return;

    dueBookings.forEach((booking) => {
      const bookingId = String(booking._id || booking.id || "");
      const roomName = booking.room?.name || "Meeting room";
      firedMap[bookingId] = new Date().toISOString();

      playAlarmSound();
      toast.info(`Meeting reminder: ${roomName} is starting now`, {
        autoClose: 12000,
      });

      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Meeting Reminder", {
          body: `Your meeting in ${roomName} is starting now`,
        });
      }
    });

    localStorage.setItem(alarmStoreKey, JSON.stringify(firedMap));
  }, [alarmStoreKey, bookings, playAlarmSound]);

  useEffect(() => {
    triggerDueMeetingAlarms();
    const alarmInterval = setInterval(() => {
      triggerDueMeetingAlarms();
    }, 5000);
    return () => clearInterval(alarmInterval);
  }, [triggerDueMeetingAlarms]);

  const openCancelModal = (booking) => {
    setSelectedBooking(booking);
    setCancelReason("");
    // Auto-fill canceledBy with logged-in user's username
    setCanceledBy(user?.name || user?.email || "");
    setCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    const finalReason = cancelReason === "Other" ? customReason : cancelReason;

    if (!finalReason.trim()) {
      toast.error("Please provide a valid reason for cancellation");
      return;
    }

    // Check if booking can be cancelled (at least 30 minutes in advance)
    const now = new Date();
    const bookingStartTime = new Date(selectedBooking.startTime);
    const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000);

    if (bookingStartTime < thirtyMinutesFromNow) {
      toast.error(
        "ನೀವು ಕ್ಯಾನ್ಸಲ್ ಮಾಡುವುದಾದರೆ ೩೦ ನಿಮಿಷಗಳಿಗಿಂತ ಮುಂಚೆ ಮಾಡಬೇಕು / You must cancel at least 30 minutes in advance"
      );
      return;
    }

    try {
      await bookingsAPI.cancelBooking(selectedBooking._id, {
        reason: finalReason,
        canceledBy,
      });

      toast.success("Booking cancelled successfully");
      setCancelModalOpen(false);

      setCancelReason("");
      setCustomReason("");
      setCanceledBy("");

      fetchBookings();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to cancel booking";
      toast.error(errorMessage);
    }
  };

  const openViewModal = (booking) => {
    setViewBooking(booking);
    setViewModalOpen(true);
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatTime = (dateString) =>
    new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const resolveRoomImage = (booking) => {
    const firstImage = Array.isArray(booking?.room?.images)
      ? booking.room.images[0]
      : booking?.room?.image;

    if (!firstImage) {
      return ROOM_IMAGE_FALLBACK;
    }

    if (firstImage.startsWith("http") || firstImage.startsWith("data:")) {
      return firstImage;
    }

    return getImageUrl(firstImage);
  };

  const getRoomLocationLabel = (room) =>
    [room?.location, room?.address, room?.city, room?.state]
      .filter(Boolean)
      .slice(0, 3)
      .join(", ");

  const getStatusLabel = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized === "confirmed" || normalized === "approved") {
      return "Confirmed";
    }

    if (normalized === "cancelled") {
      return "Cancelled";
    }

    if (normalized === "completed") {
      return "Completed";
    }

    if (normalized === "pending") {
      return "Pending Approval";
    }

    return normalized
      ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
      : "Unknown";
  };

  /* eslint-disable no-duplicate-case */
  const getStatusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "confirmed":
      case "approved":
        return "text-green-700 bg-green-100 border-green-200";
      case "pending":
        return "text-amber-700 bg-amber-100 border-amber-200";
      // eslint-disable-next-line no-duplicate-case
      case "pending":
        return "â³";
      case "pending":
        return "â³";
      case "cancelled":
        return "text-red-700 bg-red-100 border-red-200";
      case "completed":
        return "text-gray-700 bg-gray-100 border-gray-200";
      default:
        return "text-slate-700 bg-slate-100 border-slate-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "confirmed":
        return "✅";
      case "cancelled":
        return "❌";
      case "completed":
        return "✔️";
      default:
        return "📋";
    }
  };

  /* eslint-enable no-duplicate-case */

  const isUpcoming = (booking) =>
    new Date(booking.startTime) > new Date() && isApprovedStatus(booking.status);

  const isPast = (booking) =>
    new Date(booking.endTime) < new Date() || booking.status === "completed";

  // Get team color for consistent display
  const getTeamColor = (teamName) => {
    if (!teamName) return "from-gray-500 to-gray-600";

    const colors = [
      "from-blue-500 to-blue-600",
      "from-green-500 to-green-600",
      "from-purple-500 to-purple-600",
      "from-red-500 to-red-600",
      "from-yellow-500 to-yellow-600",
      "from-indigo-500 to-indigo-600",
      "from-pink-500 to-pink-600",
      "from-teal-500 to-teal-600",
      "from-orange-500 to-orange-600",
      "from-cyan-500 to-cyan-600",
      "from-emerald-500 to-emerald-600",
      "from-violet-500 to-violet-600",
    ];

    // Generate consistent hash from team name
    let hash = 0;
    for (let i = 0; i < teamName.length; i++) {
      const char = teamName.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // Check if a date has bookings
  const hasBookingsOnDate = (date) => {
    const dateStr = date.toLocaleDateString("en-US");
    return bookings.some((booking) => {
      const bookingDate = new Date(booking.startTime).toLocaleDateString(
        "en-US"
      );
      return bookingDate === dateStr;
    });
  };

  // Filter bookings based on selected date
  const filterBySelectedDate = (booking) => {
    if (!showCalendar) return true;

    const bookingDate = new Date(booking.startTime).toLocaleDateString("en-US");
    const selectedDateStr = selectedDate.toLocaleDateString("en-US");

    return bookingDate === selectedDateStr;
  };

  let filteredBookings = bookings
    .filter((booking) => {
      switch (filter) {
        case "upcoming":
          return isUpcoming(booking);
        case "past":
          return isPast(booking);
        case "cancelled":
          return booking.status === "cancelled";
        case "confirmed":
          return isApprovedStatus(booking.status);
        case "completed":
          return booking.status === "completed";
        default:
          return true;
      }
    })
    .filter(filterBySelectedDate)
    .filter((booking) => {
      if (!search.trim()) return true;
      const searchText = search.toLowerCase();
      return (
        booking.room?.name?.toLowerCase().includes(searchText) ||
        booking.purpose?.toLowerCase().includes(searchText) ||
        booking.status?.toLowerCase().includes(searchText) ||
        (booking.teamInfo?.name &&
          booking.teamInfo.name.toLowerCase().includes(searchText)) ||
        (booking.team && booking.team.toLowerCase().includes(searchText)) ||
        (booking.teamInfo?.teamLeader?.name &&
          booking.teamInfo.teamLeader.name
            .toLowerCase()
            .includes(searchText)) ||
        (booking.selectedMembers &&
          booking.selectedMembers.some(
            (member) =>
              (member.name && member.name.toLowerCase().includes(searchText)) ||
              (member.email && member.email.toLowerCase().includes(searchText))
          ))
      );
    });

  filteredBookings = filteredBookings.sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.startTime) - new Date(a.startTime);
    } else if (sortBy === "oldest") {
      return new Date(a.startTime) - new Date(b.startTime);
    } else if (sortBy === "room") {
      return (a.room?.name || "").localeCompare(b.room?.name || "");
    }
    return 0;
  });

  // Pagination logic
  const totalItems = filteredBookings.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);
  const totalUpcoming = bookings.filter(isUpcoming).length;
  const totalConfirmed = bookings.filter((booking) =>
    isApprovedStatus(booking.status)
  ).length;
  const totalPending = bookings.filter(
    (booking) => String(booking.status || "").toLowerCase() === "pending"
  ).length;
  const totalCancelled = bookings.filter(
    (booking) => String(booking.status || "").toLowerCase() === "cancelled"
  ).length;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search, showCalendar, selectedDate]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    if (!filteredBookings || filteredBookings.length === 0) {
      toast.error("No bookings to export");
      return;
    }

    const dataToExport = filteredBookings.map((b) => ({
      Room: b.room?.name || "N/A",
      Team: b.teamInfo?.name || b.team || "N/A",
      "Team Leader": b.teamInfo?.teamLeader?.name || "N/A",
      Purpose: b.purpose,
      Attendees: b.selectedMembers
        ? b.selectedMembers
            .map((m) => m.name || m.email || "Unknown")
            .join(", ")
        : "N/A",
      Status: b.status,
      "Start Time": formatDate(b.startTime) + " " + formatTime(b.startTime),
      "End Time": formatDate(b.endTime) + " " + formatTime(b.endTime),
      "Cancel Reason": b.cancelReason || "-",
      "Cancelled By": b.canceledBy || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "my-bookings.xlsx");
    toast.success("Bookings exported successfully!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-6 animate-spin">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
          </div>
          <p className="text-xl text-gray-600 font-medium">
            Loading your bookings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 page-content">
      {/* Header */}
      <div className="bg-transparent px-4 py-3">
        <div className="my-bookings-header-shell mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="my-bookings-back-button"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span className="font-medium">Go Back</span>
          </button>
          <div className="my-bookings-page-heading">
            <h1 className="my-bookings-page-title">My Bookings</h1>
            <p className="my-bookings-page-subtitle">
              Review every room reservation, status update, and meeting detail in one place.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 py-4 relative z-10">
        <div className="my-bookings-shell mx-auto">
          <section className="my-bookings-hero">
            <div className="my-bookings-stats">
              <div className="my-bookings-stat-card">
                <span className="my-bookings-stat-label">Total</span>
                <strong className="my-bookings-stat-value">{bookings.length}</strong>
              </div>
              <div className="my-bookings-stat-card">
                <span className="my-bookings-stat-label">Upcoming</span>
                <strong className="my-bookings-stat-value">{totalUpcoming}</strong>
              </div>
              <div className="my-bookings-stat-card">
                <span className="my-bookings-stat-label">Confirmed</span>
                <strong className="my-bookings-stat-value">{totalConfirmed}</strong>
              </div>
              <div className="my-bookings-stat-card">
                <span className="my-bookings-stat-label">Pending</span>
                <strong className="my-bookings-stat-value">{totalPending}</strong>
              </div>
              <div className="my-bookings-stat-card">
                <span className="my-bookings-stat-label">Cancelled</span>
                <strong className="my-bookings-stat-value">{totalCancelled}</strong>
              </div>
            </div>
          </section>

          {/* Controls */}
          <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-4 border border-white/60 mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-400/20 to-pink-600/20 rounded-full -translate-y-20 translate-x-20"></div>

            <div className="relative z-10">
              <div className="flex flex-wrap gap-3 mb-6">
                {/* Filter Dropdown */}
                <div className="relative flex-1 min-w-[200px]">
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm font-semibold text-gray-700 appearance-none cursor-pointer"
                  >
                    <option value="all">📋 All ({bookings.length})</option>
                    <option value="upcoming">
                      🚀 Upcoming ({bookings.filter(isUpcoming).length})
                    </option>
                    <option value="past">
                      📅 History ({bookings.filter(isPast).length})
                    </option>
                    <option value="cancelled">
                      ❌ Cancelled (
                      {bookings.filter((b) => b.status === "cancelled").length})
                    </option>
                    <option value="confirmed">
                      ✅ Confirmed (
                      {bookings.filter((b) => b.status === "confirmed").length})
                    </option>
                    <option value="completed">
                      ✔️ Completed (
                      {bookings.filter((b) => b.status === "completed").length})
                    </option>
                  </select>
                  <svg
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>

                {/* Calendar Toggle Button */}
                <button
                  onClick={() => setShowCalendar(!showCalendar)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center space-x-2 ${
                    showCalendar
                      ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span>📅</span>
                  <span>Calendar View</span>
                </button>
              </div>

              {/* Calendar */}
              {showCalendar && (
                <div className="mb-6 bg-white rounded-xl p-4 shadow-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-800">
                      Select Date
                    </h3>
                    <button
                      onClick={() => setShowCalendar(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <Calendar
                    onChange={setSelectedDate}
                    value={selectedDate}
                    tileClassName={({ date, view }) => {
                      if (view === "month" && hasBookingsOnDate(date)) {
                        return "bg-blue-100 text-blue-800 font-bold rounded-full";
                      }
                    }}
                  />
                  <div className="mt-4 text-center">
                    <p className="text-gray-700">
                      Selected:{" "}
                      <span className="font-semibold">
                        {selectedDate.toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-18 0 7 7 0 0118 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by room, purpose, status..."
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                >
                  <option value="newest">📅 Newest First</option>
                  <option value="oldest">📅 Oldest First</option>
                  <option value="room">🏢 By Room Name</option>
                </select>

                <select
                  value={itemsPerPage}
                  onChange={(e) =>
                    handleItemsPerPageChange(Number(e.target.value))
                  }
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                >
                  <option value={5}>📄 5 per page</option>
                  <option value={12}>📄 12 per page</option>
                  <option value={25}>📄 25 per page</option>
                  <option value={50}>📄 50 per page</option>
                </select>

                <button
                  onClick={exportToExcel}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>Export Excel</span>
                </button>
              </div>
            </div>
          </div>

          {/* Bookings Table */}
          {filteredBookings.length === 0 ? (
            <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-16 border border-white/60 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                No bookings found
              </h3>
              <p className="text-gray-600 text-lg mb-8">
                {filter === "all" && !showCalendar
                  ? "You haven't made any room bookings yet."
                  : showCalendar
                  ? `No bookings found for ${selectedDate.toLocaleDateString(
                      "en-US",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}.`
                  : `No ${filter} bookings found.`}
              </p>
              <button
                onClick={() => navigate("/meetings/book")}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Book Your First Room
              </button>
            </div>
          ) : (
            <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/60 overflow-hidden">
              <div className="my-bookings-grid-shell">
                <div className="my-bookings-grid">
                  {paginatedBookings.map((booking) => {
                    const roomImage = resolveRoomImage(booking);
                    const attendees = Array.isArray(booking.selectedMembers)
                      ? booking.selectedMembers
                      : [];
                    const visibleAttendees = expandedMembers[booking._id]
                      ? attendees
                      : attendees.slice(0, 3);

                    return (
                      <article key={booking._id} className="my-booking-card">
                        <div className="my-booking-card-media">
                          <img
                            src={roomImage}
                            alt={booking.room?.name || "Meeting room"}
                            className="my-booking-room-image"
                          />
                          <div className="my-booking-card-overlay"></div>
                          <div className="my-booking-card-topbar">
                            <span className={`my-booking-status-chip ${getStatusColor(booking.status)}`}>
                              <span>{getStatusLabel(booking.status)}</span>
                            </span>
                            <span className="my-booking-date-chip">
                              {formatDate(booking.startTime)}
                            </span>
                          </div>
                        </div>

                        <div className="my-booking-card-body">
                          <div className="my-booking-card-heading">
                            <div>
                              <p className="my-booking-card-kicker">Booked Room</p>
                              <h3 className="my-booking-room-title">
                                {booking.room?.name || "Meeting Room"}
                              </h3>
                              <p className="my-booking-room-location">
                                {getRoomLocationLabel(booking.room) ||
                                  "Workspace location will appear here"}
                              </p>
                            </div>
                            <div
                              className={`my-booking-team-badge bg-gradient-to-br ${getTeamColor(
                                booking.teamInfo?.name || booking.team
                              )}`}
                            >
                              {(booking.teamInfo?.name || booking.team || "T")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          </div>

                          <div className="my-booking-meta-grid">
                            <div className="my-booking-meta-item">
                              <span className="my-booking-meta-label">Purpose</span>
                              <strong>{booking.purpose || "Meeting reservation"}</strong>
                            </div>
                            <div className="my-booking-meta-item">
                              <span className="my-booking-meta-label">Time</span>
                              <strong>
                                {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                              </strong>
                            </div>
                            <div className="my-booking-meta-item">
                              <span className="my-booking-meta-label">Team</span>
                              <strong>
                                {booking.teamInfo?.name || booking.team || "No Team"}
                              </strong>
                            </div>
                            <div className="my-booking-meta-item">
                              <span className="my-booking-meta-label">Leader</span>
                              <strong>
                                {booking.teamInfo?.teamLeader?.name || "Not specified"}
                              </strong>
                            </div>
                          </div>

                          {attendees.length > 0 && (
                            <div className="my-booking-attendees">
                              <div className="my-booking-section-title-row">
                                <span className="my-booking-section-title">Attendees</span>
                                {attendees.length > 3 && (
                                  <button
                                    type="button"
                                    onClick={() => toggleMemberExpand(booking._id)}
                                    className="my-booking-inline-button"
                                  >
                                    {expandedMembers[booking._id]
                                      ? "Show less"
                                      : `Show all (${attendees.length})`}
                                  </button>
                                )}
                              </div>
                              <div className="my-booking-attendee-list">
                                {visibleAttendees.map((member, index) => (
                                  <span
                                    key={`${booking._id}-member-${index}`}
                                    className="my-booking-attendee-pill"
                                  >
                                    {member.name || member.email || "Member"}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="my-booking-card-actions">
                            <button
                              onClick={() => openViewModal(booking)}
                              className="my-booking-action my-booking-action-primary"
                              title="View Details"
                            >
                              View Details
                            </button>

                            {isApprovedStatus(booking.status) && isUpcoming(booking) && (
                              <button
                                onClick={() => openCancelModal(booking)}
                                className="my-booking-action my-booking-action-secondary"
                                title="Cancel Booking"
                              >
                                Cancel Booking
                              </button>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>

              <div className="hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Team Name
                      </th>
                      <th
                        scope="col"
                        className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Room
                      </th>
                      <th
                        scope="col"
                        className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedBookings.map((booking) => (
                      <tr
                        key={booking._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-3 md:px-6 py-3 md:py-4">
                          <div className="flex items-center">
                            <div
                              className={`flex-shrink-0 h-8 w-8 md:h-10 md:w-10 rounded-full flex items-center justify-center text-white font-bold text-xs md:text-base bg-gradient-to-br ${getTeamColor(
                                booking.teamInfo?.name || booking.team
                              )}`}
                            >
                              {(booking.teamInfo?.name || booking.team || "T")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <div className="ml-2 md:ml-4 min-w-0 flex-1">
                              <div className="text-xs md:text-sm font-medium text-gray-900 truncate">
                                {booking.teamInfo?.name ||
                                  booking.team ||
                                  "No Team"}
                              </div>
                              {/* Mobile: Show Leader and Room in one line */}
                              <div className="md:hidden text-xs text-gray-600 truncate">
                                {booking.teamInfo?.teamLeader?.name && (
                                  <span>
                                    👤 {booking.teamInfo.teamLeader.name}
                                  </span>
                                )}
                                {booking.teamInfo?.teamLeader?.name &&
                                  booking.room?.name && (
                                    <span className="mx-1">•</span>
                                  )}
                                {booking.room?.name && (
                                  <span>🏢 {booking.room.name}</span>
                                )}
                              </div>
                              {/* Desktop: Show only Leader */}
                              <div className="hidden md:block text-sm text-gray-500">
                                {booking.teamInfo?.teamLeader?.name
                                  ? `Leader: ${booking.teamInfo.teamLeader.name}`
                                  : ""}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${
                                booking.status === "confirmed"
                                  ? "bg-gradient-to-br from-green-500 to-emerald-600"
                                  : booking.status === "cancelled"
                                  ? "bg-gradient-to-br from-red-500 to-red-600"
                                  : "bg-gradient-to-br from-blue-500 to-indigo-600"
                              }`}
                            >
                              {booking.room?.name?.charAt(0) || "R"}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {booking.room?.name || "Room"}
                              </div>
                              <div className="text-sm text-gray-500">
                                {formatDate(booking.startTime)}{" "}
                                {formatTime(booking.startTime)} -{" "}
                                {formatTime(booking.endTime)}
                              </div>
                              <div
                                className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${getStatusColor(
                                  booking.status
                                )}`}
                              >
                                {getStatusIcon(booking.status)}{" "}
                                {booking.status.charAt(0).toUpperCase() +
                                  booking.status.slice(1)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 md:px-6 py-3 md:py-4 text-sm font-medium">
                          <div className="flex space-x-1 md:space-x-2">
                            <button
                              onClick={() => openViewModal(booking)}
                              className="text-blue-600 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 px-2 md:px-3 py-1 rounded-lg transition-colors flex items-center space-x-1"
                              title="View Details"
                            >
                              <svg
                                className="w-3 h-3 md:w-4 md:h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                              <span className="text-xs md:text-sm">View</span>
                            </button>

                            {booking.status === "confirmed" &&
                              isUpcoming(booking) && (
                                <button
                                  onClick={() => openCancelModal(booking)}
                                  className="text-yellow-600 hover:text-yellow-900 bg-yellow-100 hover:bg-yellow-200 px-2 md:px-3 py-1 rounded-lg transition-colors flex items-center space-x-1"
                                  title="Cancel Booking"
                                >
                                  <svg
                                    className="w-3 h-3 md:w-4 md:h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                  <span className="text-xs md:text-sm">
                                    Cancel
                                  </span>
                                </button>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-4 border border-white/60 mt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {startIndex + 1} to{" "}
                      {Math.min(endIndex, totalItems)} of {totalItems} bookings
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Previous Button */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          currentPage === 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>

                      {/* Page Numbers */}
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNumber;
                          if (totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handlePageChange(pageNumber)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                currentPage === pageNumber
                                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                      )}

                      {/* Next Button */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          currentPage === totalPages
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Cancel Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full max-w-md relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-red-400/20 to-rose-600/20 rounded-full -translate-y-20 translate-x-20"></div>

            <div className="p-6 relative z-10">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-rose-600 text-white rounded-2xl flex items-center justify-center mr-3 shadow-lg">
                  <svg
                    className="w-7 h-7"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Cancel Booking
                </h2>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Reason for cancellation *
                  </label>
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:ring-4 focus:ring-red-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  >
                    <option value="">-- Select Reason --</option>
                    <option value="No meeting today">No meeting today</option>
                    <option value="Room not required">Room not required</option>
                    <option value="Members not available">
                      Members not available
                    </option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {cancelReason === "Other" && (
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Custom reason *
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:ring-4 focus:ring-red-500/20 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                      placeholder="Enter custom reason"
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Cancelled By
                  </label>
                  <div className="w-full px-4 py-3 border-2 border-green-200 rounded-xl bg-green-50 flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                      {canceledBy.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {canceledBy}
                      </p>
                      <p className="text-xs text-green-600 flex items-center">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Auto-filled from your account
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setCancelModalOpen(false)}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-all duration-200"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white font-bold rounded-xl hover:from-red-600 hover:to-rose-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Booking Modal - Simplified version, full modal code would be too long */}
      {viewModalOpen && viewBooking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
            <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Booking Details
              </h2>
              <button
                onClick={() => setViewModalOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg
                  className="w-6 h-6 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-8">
              {/* Room Info */}
              <div className="mb-8">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="my-booking-modal-image-wrap">
                    <img
                      src={resolveRoomImage(viewBooking)}
                      alt={viewBooking.room?.name || "Meeting room"}
                      className="my-booking-modal-image"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {viewBooking.room?.name || "Room"}
                    </h3>
                    <p className="text-gray-500">
                      {getRoomLocationLabel(viewBooking.room) || "Meeting Room"}
                    </p>
                  </div>
                  <div className="ml-auto">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-bold border ${getStatusColor(
                        viewBooking.status
                      )} flex items-center space-x-1`}
                    >
                      <span>{getStatusLabel(viewBooking.status)}</span>
                    </span>
                  </div>
                </div>

                {viewBooking.room?.description && (
                  <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">
                    {viewBooking.room.description}
                  </p>
                )}
              </div>

              {/* Team Info */}
              {(viewBooking.teamInfo || viewBooking.team) && (
                <div
                  className={`mb-8 bg-gradient-to-r ${getTeamColor(
                    viewBooking.teamInfo?.name || viewBooking.team
                  )
                    .replace("from-", "from-")
                    .replace("to-", "to-")
                    .replace("-500", "-50")
                    .replace(
                      "-600",
                      "-100"
                    )} p-6 rounded-2xl border border-gray-200`}
                >
                  <h4 className="font-bold text-lg text-gray-900 mb-4 flex items-center space-x-2">
                    <div
                      className={`w-8 h-8 rounded-full bg-gradient-to-br ${getTeamColor(
                        viewBooking.teamInfo?.name || viewBooking.team
                      )} text-white flex items-center justify-center font-bold text-sm`}
                    >
                      {(viewBooking.teamInfo?.name || viewBooking.team || "T")
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <span>Team Information</span>
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Team Name</p>
                      <p className="text-gray-800 font-semibold text-lg">
                        {viewBooking.teamInfo?.name ||
                          viewBooking.team ||
                          "Unknown Team"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Team Leader</p>
                      <p className="text-gray-800 font-semibold">
                        {viewBooking.teamInfo?.teamLeader?.name ||
                          "Unknown Leader"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Date & Time */}
              <div
                className={`p-6 rounded-2xl mb-8 ${
                  viewBooking.status === "confirmed"
                    ? "bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100"
                    : "bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100"
                }`}
              >
                <h4 className="font-bold text-lg mb-4 flex items-center space-x-2">
                  <svg
                    className={`w-6 h-6 ${
                      viewBooking.status === "confirmed"
                        ? "text-green-600"
                        : "text-blue-600"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3a2 2 0 012-2h8a2 2 0 012 2v4m0 0V7a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2m8 0V9a2 2 0 00-2-2H8a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V9a2 2 0 00-2-2z"
                    />
                  </svg>
                  <span
                    className={
                      viewBooking.status === "confirmed"
                        ? "text-green-900"
                        : "text-blue-900"
                    }
                  >
                    Date & Time
                  </span>
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <svg
                      className={`w-5 h-5 ${
                        viewBooking.status === "confirmed"
                          ? "text-green-600"
                          : "text-blue-600"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3a2 2 0 012-2h8a2 2 0 012 2v4m0 0V7a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2m8 0V9a2 2 0 00-2-2H8a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V9a2 2 0 00-2-2z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p
                        className={`font-medium ${
                          viewBooking.status === "confirmed"
                            ? "text-green-900"
                            : "text-blue-900"
                        }`}
                      >
                        {formatDate(viewBooking.startTime)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <svg
                      className={`w-5 h-5 ${
                        viewBooking.status === "confirmed"
                          ? "text-green-600"
                          : "text-blue-600"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-500">Time</p>
                      <p
                        className={`font-medium ${
                          viewBooking.status === "confirmed"
                            ? "text-green-900"
                            : "text-blue-900"
                        }`}
                      >
                        {formatTime(viewBooking.startTime)} -{" "}
                        {formatTime(viewBooking.endTime)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purpose */}
              <div className="mb-8">
                <h4 className="font-bold text-lg text-gray-900 mb-3 flex items-center space-x-2">
                  <span>🎯</span>
                  <span>Purpose</span>
                </h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                  {viewBooking.purpose}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-gray-200">
                {viewBooking.status === "confirmed" &&
                  isUpcoming(viewBooking) && (
                    <button
                      onClick={() => {
                        setViewModalOpen(false);
                        openCancelModal(viewBooking);
                      }}
                      className="flex-1 px-6 py-3 bg-yellow-100 text-yellow-700 font-semibold rounded-xl hover:bg-yellow-200 transition-all duration-200 border border-yellow-200 hover:border-yellow-300 flex items-center justify-center space-x-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      <span>Cancel Booking</span>
                    </button>
                  )}
                <button
                  onClick={() => setViewModalOpen(false)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBookings;

